import React, { useState } from 'react';
import CustomModal from 'components/DesignSystems/New/CustomModal';
import PratusSelect, {
  OptionType,
} from 'components/DesignSystems/New/PratusSelect';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useGetPositionsForIncident } from 'hooks/incident/useRoster';
import {
  useCreateWorkAssignmentListAttachment,
  WorkAssignmentListAttachmentCreateBody,
} from 'components/IAP/hooks/assignmentListHooks';
import { WorkAssignment } from 'components/IAP/hooks/workAssignmentHooks';

export type PickerMode = 'from215' | 'fromScratch';

type CreateAssignmentCardDialogProps = {
  show: boolean;
  onClose: () => void;
  pickerMode: PickerMode;
  alreadyAddedWorkGroupIds: string[];
  periodNumber: number;
  workAssignments?: WorkAssignment[];
};

const CreateAssignmentCardDialog: React.FC<CreateAssignmentCardDialogProps> = ({
  show,
  onClose,
  pickerMode,
  alreadyAddedWorkGroupIds,
  periodNumber,
  workAssignments,
}) => {
  const [selectedWorkGroupId, setSelectedWorkGroupId] = useState<string | null>(
    null
  );
  const { mutate: createWala, isPending } =
    useCreateWorkAssignmentListAttachment();
  const { data: positions } = useGetPositionsForIncident();

  const workGroupOptions = React.useMemo(() => {
    if (pickerMode === 'from215') {
      const seen = new Set<string>();
      return (workAssignments ?? [])
        .filter(
          (wa) =>
            wa.operational_period_number === periodNumber && !!wa.work_group_id
        )
        .reduce<{ value: string; label: string | React.ReactNode }[]>(
          (acc, wa) => {
            const id = wa.work_group_id!;
            if (seen.has(id)) return acc;
            seen.add(id);
            const alreadyAdded = alreadyAddedWorkGroupIds.includes(id);
            acc.push({
              value: id,
              label: alreadyAdded ? (
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip id={`tooltip-${id}`}>Already added</Tooltip>
                  }
                >
                  <span>
                    {wa.work_group_name ?? id}{' '}
                    <em className="text-muted">(Already added)</em>
                  </span>
                </OverlayTrigger>
              ) : (
                (wa.work_group_name ?? id)
              ),
            });
            return acc;
          },
          []
        );
    }

    // fromScratch: all work groups from incident positions
    return (positions ?? [])
      .filter((p) => p.is_workgroup && !p.isResourcePosition)
      .map((p) => {
        const alreadyAdded = alreadyAddedWorkGroupIds.includes(p.id);
        return {
          value: p.id,
          label: alreadyAdded ? (
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip id={`tooltip-${p.id}`}>Already added</Tooltip>}
            >
              <span>
                {p.name} <em className="text-muted">(Already added)</em>
              </span>
            </OverlayTrigger>
          ) : (
            p.name
          ),
        };
      });
  }, [
    pickerMode,
    workAssignments,
    positions,
    periodNumber,
    alreadyAddedWorkGroupIds,
  ]);

  const isOptionDisabled = (option: { value: string | boolean }) =>
    typeof option.value === 'string' &&
    alreadyAddedWorkGroupIds.includes(option.value);

  const handleConfirm = () => {
    if (!selectedWorkGroupId) return;
    const body: WorkAssignmentListAttachmentCreateBody = {
      workGroupId: selectedWorkGroupId,
      periodNumber,
    };
    createWala(body, {
      onSuccess: () => {
        setSelectedWorkGroupId(null);
        onClose();
      },
    });
  };

  const handleClose = () => {
    setSelectedWorkGroupId(null);
    onClose();
  };

  const heading =
    pickerMode === 'from215'
      ? 'Add Assignment Card from ICS 215'
      : 'Add Assignment Card from scratch';

  return (
    <CustomModal
      show={show}
      heading={heading}
      onClose={handleClose}
      onConfirm={handleConfirm}
      saveButton="Add"
      buttonDisable={!selectedWorkGroupId || isPending}
      staticBackdrop={false}
    >
      <PratusSelect
        options={workGroupOptions}
        value={
          workGroupOptions.find((o) => o.value === selectedWorkGroupId) ?? null
        }
        onChange={(opt) => {
          const single =
            opt !== null && !Array.isArray(opt) ? (opt as OptionType) : null;
          setSelectedWorkGroupId(single ? String(single.value) : null);
        }}
        isOptionDisabled={isOptionDisabled}
        placeholder="Select a work group..."
      />
    </CustomModal>
  );
};

export default CreateAssignmentCardDialog;
